import { fetchAPI } from './client';
import { News, ApiResponse } from './types';

export interface GetNewsParams {
  category?: string;
  published?: boolean;
  lang?: string;
}

/**
 * 뉴스 목록 조회
 */
export async function getNews(params: GetNewsParams = {}): Promise<News[]> {
  const { category, published = true, lang = 'ko' } = params;

  const queryParams = new URLSearchParams();
  if (category) queryParams.append('category', category);
  queryParams.append('published', published.toString());
  queryParams.append('lang', lang);

  const query = queryParams.toString();
  return fetchAPI<News[]>(`/news${query ? `?${query}` : ''}`);
}

/**
 * 뉴스 상세 조회
 */
export async function getNewsById(id: number, lang: string = 'ko'): Promise<News> {
  return fetchAPI<News>(`/news/${id}?lang=${lang}`);
}

export interface CreateNewsData {
  category: string;
  thumbnailUrl?: string;
  isPublished: boolean;
  translations: {
    ko: {
      title: string;
      excerpt?: string;
      content: string;
    };
    en?: {
      title?: string;
      excerpt?: string;
      content?: string;
    };
  };
}

export interface UpdateNewsData extends CreateNewsData {
  id: number;
}

/**
 * 뉴스 생성 (관리자 전용)
 */
export async function createNews(data: CreateNewsData, token: string): Promise<News> {
  const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api`;

  const response = await fetch(`${API_BASE_URL}/news`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create news: ${response.status} ${response.statusText}`);
  }

  const result: ApiResponse<News> = await response.json();
  return result.data;
}

/**
 * 뉴스 수정 (관리자 전용)
 */
export async function updateNews(data: UpdateNewsData, token: string): Promise<News> {
  const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api`;

  const response = await fetch(`${API_BASE_URL}/news/${data.id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to update news: ${response.status} ${response.statusText}`);
  }

  const result: ApiResponse<News> = await response.json();
  return result.data;
}

/**
 * 뉴스 삭제 (관리자 전용)
 */
export async function deleteNews(id: number, token: string): Promise<boolean> {
  const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api`;

  const response = await fetch(`${API_BASE_URL}/news/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete news: ${response.status} ${response.statusText}`);
  }

  const data: ApiResponse<null> = await response.json();
  return data.success;
}
